import React, { MouseEvent, ReactElement, useMemo } from 'react';
import { TopBarGroupType } from 'b2b-components/theme/components/Header/TopBar';
import { ENTRY_ID_TYPE, useEntry } from 'b2b-apps/shop/content/hooks/useEntry';
import { useActiveLanguage, useChangeLanguageAction } from 'b2b-apps/shop/i18n/hooks';
import { getAvailableLanguages, LANGUAGE_LABELS } from 'b2b-apps/shop/i18n/utils/language';
import { useRouterPush } from 'b2b-apps/shop/routes/hooks';
import { openLink } from 'b2b-apps/shop/routes/utils/url';
import { LANGUAGE_CODE } from 'b2b-common/core/i18n/consts';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

export const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const MAX_LANGUAGE_COUNT = 3;
    const push = useRouterPush();
    const activeLanguage = useActiveLanguage();
    const changeLanguage = useChangeLanguageAction();
    const content = useEntry(ENTRY_ID_TYPE.header);
    const parsedLanguages = getAvailableLanguages().map((languageCode: LANGUAGE_CODE) => ({
      code: languageCode,
      label: LANGUAGE_LABELS[languageCode],
    }));

    const linksGroups = useMemo(
      () => {
        if (
          !content.isLoaded
          || !content.data
          || !Array.isArray(content.data.group)
        ) {
          return [];
        }

        return content.data.group.map((group: TopBarGroupType<LANGUAGE_CODE>) => ({
          name: group.name,
          links: group.links,
        }));
      },
      [content],
    );

    const handleLinkClick = (url: string, event?: MouseEvent): void => {
      openLink(url, push, event);
    };

    const injectedProps: InjectedProps = {
      linksGroups,
      onLinkClick: handleLinkClick,
      activeLanguage,
      languages: parsedLanguages,
      onLanguageChange: changeLanguage,
      isDropdown: parsedLanguages.length >= MAX_LANGUAGE_COUNT,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return WithContainer;
};
